/**
 * Each environment its own layout of windows: the window manager keeps one per environment, and
 * a new one starts from {@link firstLayout}. Here in `app/` because it joins two services that
 * may not use each other: the environments (`state/`) and the window manager (`wm/`).
 */
import { createEffect } from "solid-js";
import * as L from "../wm/layout";
import { isExtraWindow, type WindowDef, type WindowManager } from "../wm/manager";
import { ENVIRONMENT_GROUPS, type Environment, type Environments } from "../state/environments";
import type { Plugins } from "../state/plugins";

/**
 * The first layout of `environment`, from the default one `base` (the config's or the built-in):
 * the windows of {@link ENVIRONMENT_GROUPS} it does not list closed, the plugin windows and the
 * windows meant for environments (`byEnvironment`) it lists opened, in its order, docked where
 * they ask (the default layout cannot know them). Other built-in windows it lists stay as the
 * default layout has them, so a config closing one is obeyed. It lists none, or there is no
 * environment: `base` unchanged.
 */
export function firstLayout(base: L.Layout, environment: Environment | null, windows: readonly WindowDef[]): L.Layout {
  if (!environment?.windows.length) return base;
  const chosen = environment.windows;
  const closed = windows.filter((w) => ENVIRONMENT_GROUPS.includes(w.group ?? "") && !chosen.includes(w.id)).reduce((layout, w) => (L.isOpen(layout, w.id) ? L.close(layout, w.id) : layout), base);
  return chosen.reduce((layout, id) => {
    const def = windows.find((w) => w.id === id);
    if (!def || L.isOpen(layout, id) || !(isExtraWindow(id) || def.byEnvironment)) return layout;
    const near = def.byEnvironment && L.areaOf(layout.root, def.byEnvironment.beside);
    return near ? L.activate(L.dock(layout, id, near.id, def.byEnvironment!.zone, def.byEnvironment!.share), id) : L.open(layout, id);
  }, closed);
}

/** Has the window manager use the layout of the environment worked in, from when the plugin list arrived (so the first one keeps the screen's). */
export function followEnvironmentLayouts(environments: Pick<Environments, "current">, plugins: Pick<Plugins, "list">, wm: WindowManager): void {
  createEffect(() => {
    if (!plugins.list().length) return;
    const environment = environments.current();
    wm.useScope(environment?.id ?? null, (base) => firstLayout(base, environment, wm.windows()));
  });
}
